import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Input, message, Select, Space, Table} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PersonnelData } from '../../../../types/PersonnelData';
import { fetchSelectOptionsByContractNO } from "../../../../backendInteraction/ApiFetch";
import {
    addPersonnelRow, deletePersonnelByPid,
    fetchPersonnelByPid, fetchTotalPersonnelNo,
    updatePersonnelRow
} from "../../../../backendInteraction/PersonnelApiFetch";
import {
    calculateDurationBetweenDates,
    formatDateToClockTime,
    parseClockTimeToDate,
    WorkingTimeValueValidator
} from "../../../../utils/Validator";

interface PersonnelTableProps {
    data: PersonnelData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    onTotalPersonnelUpdate?: (total: number) => void;
}

const PersonnelTable: React.FC<PersonnelTableProps> = ({ data, plodId, contractNo, isView,
                                                           isAddNewRow, setIsAddNewRow, onTotalPersonnelUpdate }) => {
    const [personnelData, setPersonnelData] = useState<PersonnelData []>([]);

    const [positionOptions, setPositionOptions] = useState<{value: string; label: string}[]>([]);
    const [nameOptions, setNameOptions] = useState<{value: string; label: string}[]>([]);

    const [editingPerId, setEditingPerId] = useState<number | null>(null);
    const [editData, setEditData] = useState<PersonnelData | null>(null);

    const dayOption: { label: string; value: string }[] = [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
    ];

    const loadOptions = useCallback(async () => {
        if (contractNo) {
            const positions = await fetchSelectOptionsByContractNO(contractNo, "Position");
            const names = await fetchSelectOptionsByContractNO(contractNo, "Name");
            setPositionOptions(positions.map(position => ({ value: position, label: position })));
            setNameOptions(names.map(name => ({ value: name, label: name })));
        } else {
            setPositionOptions([]);
            setNameOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        setPersonnelData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingPerId !== -1) {
            const currentDate = new Date();
            const defaultMeridiem = currentDate.getHours() >= 6 && currentDate.getHours() < 18 ? 'Day' : 'Night';

            const newRow: PersonnelData = {
                key: -1,
                per_id: -1,
                position: "",
                name: "",
                time_start: `06:15 ${defaultMeridiem === 'Day' ? 'AM' : 'PM'}`,
                time_finish: `06:15 ${defaultMeridiem === 'Day' ? 'PM' : 'AM'}`,
                total_hours: "",
                travel_day: "",
                sick_day: "",
                pid: plodId ?? -1,
            };
            setPersonnelData(prevData => {
                const hasNewRow = prevData.some(item => item.per_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingPerId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingPerId]);

    useEffect(() => {
        if (isView) {
            if (editingPerId === -1) {
                setPersonnelData(prevData => prevData.filter(item => item.per_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingPerId(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { time_start, time_finish } = editData;
        if (!WorkingTimeValueValidator(time_start, time_finish)) return;

        const start = parseClockTimeToDate(time_start);
        const finish = parseClockTimeToDate(time_finish);
        const duration = calculateDurationBetweenDates(start, finish);
        const formattedStart = formatDateToClockTime(start);
        const formattedFinish = formatDateToClockTime(finish);

        const updatedEditData = { ...editData, time_start: formattedStart || '',
            time_finish: formattedFinish || '', total_hours: duration || '' };
        setEditData(updatedEditData);


        console.log('updatedEditData', updatedEditData);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingPerId === -1) {
            const success = await addPersonnelRow(updatedEditData, aid);
            if (success) {
                setPersonnelData(await fetchPersonnelByPid(plodId ?? -1, aid));
                message.success("New personnel added successfully!");

                if (onTotalPersonnelUpdate) {
                    const newTotal = await fetchTotalPersonnelNo(plodId, aid);
                    onTotalPersonnelUpdate(newTotal);
                }

            } else {
                message.error("Failed to add new personnel.");
            }
        } else {
            const success = await updatePersonnelRow(editingPerId ?? -1, updatedEditData);
            if (success) {
                setPersonnelData(await fetchPersonnelByPid(plodId ?? -1, aid));
                message.success("Personnel updated successfully!");

                if (onTotalPersonnelUpdate) {
                    const newTotal = await fetchTotalPersonnelNo(plodId, aid);
                    onTotalPersonnelUpdate(newTotal);
                }

            } else {
                message.error("Failed to update personnel.");
            }
        }

        if (editingPerId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingPerId(null);
        setEditData(null);
    }, [editData, editingPerId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: PersonnelData) => {
        console.log("Cancel editing");
        setEditingPerId(null);
        setEditData(null);

        if (record.per_id === -1) {
            setPersonnelData(prevData => prevData.filter(item => item.per_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: PersonnelData) => {
        console.log("Editing row:", record);

        if (editingPerId === -1) {
            setPersonnelData(prevData => prevData.filter(item => item.per_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingPerId(prevId => {
            if (prevId !== null) cancelEditing({ per_id: prevId } as PersonnelData);
            return record.per_id;
        });

        setEditData({ ...record });
    }, [editingPerId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: PersonnelData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deletePersonnelByPid(record.per_id);
        if (success) {
            setPersonnelData(await fetchPersonnelByPid(plodId ?? -1, aid));
            message.success("Personnel deleted successfully!");

            if (onTotalPersonnelUpdate) {
                const newTotal = await fetchTotalPersonnelNo(plodId, aid);
                onTotalPersonnelUpdate(newTotal);
            }

        } else {
            message.error("Failed to delete personnel.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof PersonnelData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<PersonnelData> = useMemo(() => [
        {
            title: 'Position',
            dataIndex: 'position',
            key: 'position',
            width: "12%",
            render: (_, record) =>
                editingPerId === record.per_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={positionOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.position || ""}
                        onChange={(value) => handleInputChange("position", value || "")}
                    />
                ) : (
                    record.position
                ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: "15%",
            render: (_, record) =>
                editingPerId === record.per_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={nameOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.name || ""}
                        onChange={(value) => handleInputChange("name", value || "")}
                    />
                ) : (
                    record.name
                ),
        },
        {
            title: 'Time Start',
            dataIndex: 'time_start',
            key: 'time_start',
            width: "14%",
            render: (_, record) =>
                editingPerId === record.per_id ? (
                    <Input
                        value={editData?.time_start}
                        onChange={(e) => handleInputChange("time_start", e.target.value)}
                    />
                ) : (
                    record.time_start
                ),
        },
        {
            title: 'Time Finish',
            dataIndex: 'time_finish',
            key: 'time_finish',
            width: "14%",
            render: (_, record) =>
                editingPerId === record.per_id ? (
                    <Input
                        value={editData?.time_finish}
                        onChange={(e) => handleInputChange("time_finish", e.target.value)}
                    />
                ) : (
                    record.time_finish
                ),
        },
        {
            title: 'Total Hours',
            dataIndex: 'total_hours',
            key: 'total_hours',
            width: "10%",
            render: (_, record) =>( record.total_hours ),
        },
        {
            title: 'Travel Day',
            dataIndex: 'travel_day',
            key: 'travel_day',
            width: "10%",
            render: (_, record) =>
                editingPerId === record.per_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={dayOption}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.travel_day || ""}
                        onChange={(value) => handleInputChange("travel_day", value || "")}
                    />
                ) : (
                    record.travel_day
                ),
        },
        {
            title: 'Sick Day',
            dataIndex: 'sick_day',
            key: 'sick_day',
            width: "10%",
            render: (_, record) =>
                editingPerId === record.per_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={dayOption}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.sick_day || ""}
                        onChange={(value) => handleInputChange("sick_day", value || "")}
                    />
                ) : (
                    record.sick_day
                ),
        },
        ...(!isView
            ? [{
                title: 'Action',
                key: 'action',
                width: "15%",
                render: (_, record) => (
                    <Space size="small">
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingPerId === record.per_id ? saveChanges() : startEditing(record))}>
                            {editingPerId === record.per_id ? "Save" : "Edit"}
                        </Button>
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingPerId === record.per_id ? cancelEditing(record) : deleteRow(record))}>
                            {editingPerId === record.per_id ? "Cancel" : "Delete"}
                        </Button>
                    </Space>
                ),
            },
            ] as ColumnsType<PersonnelData>
            : []),
    ], [isView, editingPerId, positionOptions, nameOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<PersonnelData>
            columns={columns}
            pagination={false}
            dataSource={ personnelData }
        />
    );
}

export default PersonnelTable;
